import styled, { css } from 'styled-components';
import get from 'lodash/get';

import { themeColor, themeToggleColor } from '~/common/utils/theme';
import { Typography } from '~/ui/components';
import { ColorsTypes } from '~/ui/theme/colors';
import { CSSProperties } from 'react';

// Legacy scale names lack semantic keys (.secondary/.primaryAlt) and don't adapt to dark,
// so tags using them fall back to static ['50']/['800'] (light bg + dark text in BOTH themes).
// Map them to the dark-aware semantic groups so status tags invert correctly in dark mode.
const PALETTE_MAP: Record<string, string> = {
  primary: 'brand',
  success: 'positive',
  error: 'negative',
  warning: 'alert',
  gray: 'neutral',
};
const mapPalette = (colorPalette?: string) => PALETTE_MAP[colorPalette] || colorPalette || 'neutral';

export const TagContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

export interface TagItemProps {
  style?: CSSProperties;
  className?: string;
  bgColor?: string;
  borderColor?: string;
  textColor?: string;
  colorPalette?: ColorsTypes;
  small?: boolean;
  rounded?: boolean;
  noWrap?: boolean;
  ellipsis?: boolean;
}

export const Tag = styled<TagItemProps>(Typography).attrs(
  ({ theme, colorPalette = 'neutral', textColor, small = true, rounded = true }) => ({
    element: 'span',
    type: small ? 'footnote' : 'paragraph',
    weight: rounded ? 600 : 400,
    color:
      get(theme.colors, textColor) ||
      textColor ||
      theme.colors[mapPalette(colorPalette)]?.primaryAlt ||
      theme.colors[mapPalette(colorPalette)]?.['800'],
    className: 'tag',
  }),
)`
  ${({ theme, colorPalette = 'neutral', bgColor, small, textColor, rounded, noWrap, borderColor }) => css`
    background-color: ${get(theme.colors, bgColor) ||
    bgColor ||
    theme.colors[mapPalette(colorPalette)]?.secondary ||
    theme.colors[mapPalette(colorPalette)]?.['50']};
    padding: 12px 14px 12px 12px;
    gap: 12px;
    border-radius: 0 8px 8px 0;
    display: flex;
    justify-content: center;
    align-items: baseline;
    white-space: ${noWrap ? 'nowrap' : 'normal'};

    ${borderColor &&
    css`
      border: 1px solid ${get(theme.colors, borderColor) || borderColor};
    `}

    svg {
      cursor: pointer;
      fill: ${textColor || theme.colors[mapPalette(colorPalette)]?.primaryAlt};
      flex: none;
    }

    ${small &&
    css`
      padding: 2px 10px 2px 8px;
      gap: 4px;
    `}

    ${rounded &&
    css`
      padding: ${small ? '2px 12px' : '4px 12px'};
      border-radius: 8px;
    `}
  `}

  ${({ ellipsis }) =>
    ellipsis &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    `}
`;

export const MoreTags = styled<TagItemProps>(Typography).attrs(
  ({ theme, colorPalette = 'neutral', textColor, small = true, rounded = true }) => ({
    element: 'span',
    type: small ? 'footnote' : 'paragraph',
    weight: rounded ? 600 : 400,
    color:
      get(theme.colors, textColor) ||
      textColor ||
      theme.colors[mapPalette(colorPalette)]?.primaryAlt ||
      theme.colors[mapPalette(colorPalette)]?.['800'],
    className: 'tag',
  }),
)`
  ${({ theme, colorPalette = 'neutral', bgColor, small, textColor, rounded, noWrap }) => css`
    background-color: ${get(theme.colors, bgColor) ||
    bgColor ||
    theme.colors[mapPalette(colorPalette)]?.secondary ||
    theme.colors[mapPalette(colorPalette)]?.['50']};
    padding: 12px 14px 12px 12px;
    gap: 12px;
    border-radius: 0 8px 8px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: ${noWrap ? 'nowrap' : 'normal'};
    cursor: pointer;

    svg {
      cursor: pointer;
      fill: ${textColor || theme.colors[mapPalette(colorPalette)]?.primaryAlt};
      flex: none;
    }

    ${small &&
    css`
      padding: 2px 10px 2px 8px;
      gap: 4px;
    `}

    ${rounded &&
    css`
      padding: ${small ? '2px 12px' : '4px 12px'};
      border-radius: 8px;
    `}
  `}

  ${({ ellipsis }) =>
    ellipsis &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    `}
`;

export const AddTagInput = styled(Tag).attrs(({ theme }) => ({
  element: 'div',
  color: theme.colors.element.secondary,
  bgColor: themeToggleColor(theme, 'background.secondary', { dark: 'background.secondary' }),
  weight: 600,
  rounded: true,
}))`
  cursor: pointer;
`;

export const PopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  max-width: 387px;
  min-width: 250px;
  max-height: 350px;
  height: 100vh;
  border-radius: 1rem;
`;

export const PopoverHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${themeColor('divider.primary')};
  align-items: center;

  .action-icon {
    cursor: pointer;
    color: ${themeColor('brand.primary', { dark: 'brand.primaryAlt' })};
  }
`;

export const PopoverTitle = styled.span`
  display: flex;
  font-size: 18px;
  font-weight: 700;
  color: ${themeColor('element.primary')};
`;

export const PopoverCloseAction = styled.div`
  display: flex;
  padding: 14px;
  background-color: ${themeColor('background.secondary')};
  border-radius: 12px;
  cursor: pointer;
`;

export const PopOverTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  overflow: auto;
  padding-right: 8px;
  position: relative;
`;

export const TagPopoverWrapper = styled.div`
  cursor: pointer;
`;
